// Copyright (c) StrangeBee 2023

import { ReactElement, useContext } from 'react';
import { useLocalMessage } from '@Helpers/';
import { CaseCustomField, CaseOutput, CaseStatusStage, SeverityValues } from 'thehive-sdk';
import { Link } from 'react-router-dom';
import RouteBuilder from '@Services/RouteBuilder';
import DisplayTag from '@Organisms/DisplayTag/DisplayTag';
import { DisplayCustomFields } from '@Organisms/DisplayCustomFields/DisplayCustomFields';
import { ScopeContext } from '@Context/ScopeProvider';
import { FormatDate } from '@Molecules/Format';
import { ICONS } from '@Constants/iconsApp';
import { PreviewButton } from '@Atoms/Buttons';
import { SeverityTag } from '@Atoms/Info/SeverityTag';

import './CaseListTitle.scss';

interface CaseListTitleProps {
    data: CaseOutput;
    onTagClick?: (tag: string, event: any) => void;
    onSeverityClick?: (severity: SeverityValues) => void;
    customFieldsIsOpen: boolean;
    tagsIsOpen: boolean;
    onCustomFieldsClick?: (customField: CaseCustomField) => void;
    onPreviewClick?: () => void;
    onLinkClick?: () => void;
}

const CaseListTitle = ({
    data,
    onTagClick,
    onSeverityClick,
    customFieldsIsOpen = false,
    tagsIsOpen = false,
    onCustomFieldsClick,
    onPreviewClick,
    onLinkClick,
}: CaseListTitleProps): ReactElement => {
    const {
        title,
        number: caseNumber = 0,
        endDate = 0,
        stage,
        severity = 1,
        tags = [],
        customFields = [],
        _id: caseId = '',
    } = data;

    const i = useLocalMessage();

    const { coloredTags } = useContext(ScopeContext);

    const titleContent = (
        <>
            <span className="case-list-title__link-number">#{caseNumber} - </span>
            {title}
        </>
    );

    return (
        <div className="case-list-title">
            <div className={'case-list-title__description'}>
                <SeverityTag
                    severity={severity}
                    onClick={(): void => {
                        onSeverityClick?.(severity);
                    }}
                />

                {onLinkClick ? (
                    <div className="case-list-title__link" onClick={onLinkClick}>
                        {titleContent}
                    </div>
                ) : (
                    <Link to={RouteBuilder.compute('cases.details', { caseId })} className="case-list-title__link">
                        {titleContent}
                    </Link>
                )}
            </div>

            {tagsIsOpen && (
                <div className="case-list-title__tags">
                    <ICONS.TagsOutlined className="case-list-title__icon" />
                    {tags.length ? (
                        <DisplayTag.List<string> data={tags} limit={5}>
                            {(tag) => <DisplayTag tag={tag} onClick={onTagClick} color={coloredTags[tag]} />}
                        </DisplayTag.List>
                    ) : (
                        <span className="case-additional-row__text">{i('general.none')}</span>
                    )}
                </div>
            )}

            {customFieldsIsOpen && (
                <div className="case-list-title__custom-fields">
                    <ICONS.ControlOutlined className="case-list-title__icon" />
                    {customFields.length ? (
                        <DisplayCustomFields customFields={customFields} onClick={onCustomFieldsClick} />
                    ) : (
                        <span className="case-additional-row__text">{i('general.none')}</span>
                    )}
                </div>
            )}

            {stage === CaseStatusStage.CLOSED && (
                <div className="case-list-title__closed">
                    {i('case.list.table.description.closed')} <FormatDate date={endDate} />
                </div>
            )}

            {onPreviewClick && (
                <div className="generic-drawer-preview">
                    <PreviewButton onClick={onPreviewClick} />
                </div>
            )}
        </div>
    );
};

export default CaseListTitle;
